import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Navigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { DASH } from "@/constants/testIds";
import VideoRecorder from "@/components/VideoRecorder";

const PHYSICAL = ["Musculation", "Running", "CrossFit", "Boxe", "Yoga", "Prep Physique"];
const MENTAL = [
    "Gestion du stress",
    "Confiance en soi",
    "Anti-Burnout",
    "Motivation",
    "Sortir de la solitude",
];

export default function Dashboard() {
    const { user, loading } = useAuth();
    const [profile, setProfile] = useState(null);
    const [progress, setProgress] = useState([]);
    const [requests, setRequests] = useState([]);
    const [prog, setProg] = useState({
        student_name: "",
        student_age: "",
        duration_label: "",
        headline: "",
        badge: "objectif_atteint",
        media_url: "",
        media_type: "image",
        consent: false,
    });
    const [testim, setTestim] = useState({
        quote: "",
        student: "",
        duration: "",
        video_data_url: null,
        consent: false,
    });

    useEffect(() => {
        if (user?.role !== "coach") return;
        (async () => {
            const [p, pg, r] = await Promise.all([
                api.get("/coaches/me/profile").catch(() => null),
                api.get("/coaches/me/progress").catch(() => ({ data: [] })),
                api.get("/coaches/me/requests").catch(() => ({ data: [] })),
            ]);
            if (p) {
                setProfile(p.data);
                const t = p.data.testimonial || {};
                setTestim({
                    quote: t.quote || "",
                    student: t.student || "",
                    duration: t.duration || "",
                    video_data_url: t.video_data_url || null,
                    consent: false,
                });
            }
            setProgress(pg.data);
            setRequests(r.data);
        })();
    }, [user]);

    if (loading) return <div className="p-12 text-neutral-500">Chargement...</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "coach") return <Navigate to="/login" replace />;
    if (!profile) return <div className="p-12 text-neutral-500">Chargement du tableau de bord...</div>;

    const saveProfile = async () => {
        try {
            const { data } = await api.put("/coaches/me/profile", profile);
            setProfile(data);
            toast.success("Profil enregistré");
        } catch {
            toast.error("Échec de l'enregistrement");
        }
    };

    const toggleSpec = (s) => {
        const has = profile.specialties.includes(s);
        setProfile({
            ...profile,
            specialties: has
                ? profile.specialties.filter((x) => x !== s)
                : [...profile.specialties, s],
        });
    };

    const toggleObj = (o) => {
        const has = profile.objective_tags.includes(o);
        setProfile({
            ...profile,
            objective_tags: has
                ? profile.objective_tags.filter((x) => x !== o)
                : [...profile.objective_tags, o],
        });
    };

    const submitProg = async (e) => {
        e.preventDefault();
        if (!prog.consent) {
            toast.error("Le consentement de l'élève est requis.");
            return;
        }
        try {
            const payload = {
                ...prog,
                student_age: prog.student_age ? parseInt(prog.student_age, 10) : null,
            };
            await api.post("/coaches/me/progress", payload);
            toast.success("Progression publiée");
            setProg({
                student_name: "",
                student_age: "",
                duration_label: "",
                headline: "",
                badge: "objectif_atteint",
                media_url: "",
                media_type: "image",
                consent: false,
            });
            const [pg, p] = await Promise.all([
                api.get("/coaches/me/progress"),
                api.get("/coaches/me/profile"),
            ]);
            setProgress(pg.data);
            setProfile(p.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec de l'envoi");
        }
    };

    const saveTestimonial = async (e) => {
        e.preventDefault();
        if (!testim.consent) {
            toast.error("Consentement de l'élève requis.");
            return;
        }
        if (!testim.quote || !testim.student) {
            toast.error("Citation et nom de l'élève requis.");
            return;
        }
        try {
            const payload = {
                quote: testim.quote,
                student: testim.student,
                duration: testim.duration,
            };
            // Only send a data URL if it changed (prevents re-uploading the same blob)
            if (testim.video_data_url && testim.video_data_url.startsWith("data:")) {
                payload.video_data_url = testim.video_data_url;
            }
            const { data } = await api.put("/coaches/me/testimonial", payload);
            setProfile(data);
            setTestim((prev) => ({ ...prev, consent: false, video_data_url: data.testimonial?.video_data_url || null }));
            toast.success("Témoignage enregistré");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
        }
    };

    const specList = profile.category === "physical" ? PHYSICAL : MENTAL;

    return (
        <div className="mx-auto max-w-[1400px] px-6 py-10">
            <div className="mb-8 flex items-end justify-between">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                        Centre coach
                    </div>
                    <h1 className="font-display text-5xl uppercase leading-none tracking-tight text-white">
                        Tableau de bord
                    </h1>
                </div>
                <div className="border border-white/10 bg-[#121212] p-4 text-right">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        Mérite
                    </div>
                    <div className="font-display text-4xl text-[#FF2E2E]">
                        {Math.round(profile.merit_score || 0)}
                    </div>
                </div>
            </div>

            <Tabs defaultValue="profile">
                <TabsList className="h-auto gap-0 rounded-none border border-white/10 bg-transparent p-0">
                    <TabsTrigger
                        data-testid={DASH.profileTab}
                        value="profile"
                        className="rounded-none px-4 py-2 font-mono text-[11px] uppercase tracking-widest data-[state=active]:bg-[#FF2E2E] data-[state=active]:text-white"
                    >
                        Profil
                    </TabsTrigger>
                    <TabsTrigger
                        data-testid={DASH.studentsTab}
                        value="students"
                        className="rounded-none px-4 py-2 font-mono text-[11px] uppercase tracking-widest data-[state=active]:bg-[#FF2E2E] data-[state=active]:text-white"
                    >
                        Élèves
                    </TabsTrigger>
                    <TabsTrigger
                        data-testid={DASH.uploadsTab}
                        value="uploads"
                        className="rounded-none px-4 py-2 font-mono text-[11px] uppercase tracking-widest data-[state=active]:bg-[#FF2E2E] data-[state=active]:text-white"
                    >
                        Progressions
                    </TabsTrigger>
                    <TabsTrigger
                        data-testid="dash-tab-testimonial"
                        value="testimonial"
                        className="rounded-none px-4 py-2 font-mono text-[11px] uppercase tracking-widest data-[state=active]:bg-[#FF2E2E] data-[state=active]:text-white"
                    >
                        Avis Vidéo
                    </TabsTrigger>
                </TabsList>

                {/* PROFILE */}
                <TabsContent value="profile" className="mt-6">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <div className="space-y-3 border border-white/10 bg-[#121212] p-6">
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Nom affiché
                            </Label>
                            <Input
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                                value={profile.name}
                                onChange={(e) =>
                                    setProfile({ ...profile, name: e.target.value })
                                }
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                        Âge
                                    </Label>
                                    <Input
                                        type="number"
                                        className="rounded-none border-white/10 bg-[#0a0a0a]"
                                        value={profile.age}
                                        onChange={(e) =>
                                            setProfile({
                                                ...profile,
                                                age: parseInt(e.target.value || "0", 10),
                                            })
                                        }
                                    />
                                </div>
                                <div>
                                    <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                        Tarif horaire (€)
                                    </Label>
                                    <Input
                                        type="number"
                                        className="rounded-none border-white/10 bg-[#0a0a0a]"
                                        value={profile.hourly_rate}
                                        onChange={(e) =>
                                            setProfile({
                                                ...profile,
                                                hourly_rate: parseInt(e.target.value || "0", 10),
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Ville
                            </Label>
                            <Input
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                                value={profile.city}
                                onChange={(e) =>
                                    setProfile({ ...profile, city: e.target.value })
                                }
                            />
                            <div className="flex items-center justify-between border border-white/10 p-3">
                                <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">
                                    Disponible à distance
                                </span>
                                <Switch
                                    checked={profile.remote}
                                    onCheckedChange={(v) => setProfile({ ...profile, remote: v })}
                                />
                            </div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                URL photo
                            </Label>
                            <Input
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                                value={profile.photo_url}
                                onChange={(e) =>
                                    setProfile({ ...profile, photo_url: e.target.value })
                                }
                            />
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Bio
                            </Label>
                            <Textarea
                                rows={4}
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                                value={profile.bio}
                                onChange={(e) =>
                                    setProfile({ ...profile, bio: e.target.value })
                                }
                            />
                        </div>

                        <div className="space-y-4 border border-white/10 bg-[#121212] p-6">
                            <div>
                                <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                    Catégorie
                                </Label>
                                <div className="mt-2 flex border border-white/10">
                                    {["physical", "mental"].map((c) => (
                                        <button
                                            key={c}
                                            onClick={() => setProfile({ ...profile, category: c, specialties: [] })}
                                            className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
                                                profile.category === c
                                                    ? "bg-[#FF2E2E] text-white"
                                                    : "text-neutral-400 hover:text-white"
                                            }`}
                                        >
                                            {c === "physical" ? "Corps" : "Esprit"}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                    Spécialités
                                </Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {specList.map((s) => {
                                        const active = profile.specialties.includes(s);
                                        return (
                                            <button
                                                key={s}
                                                onClick={() => toggleSpec(s)}
                                                className={`border px-3 py-1 text-xs uppercase tracking-widest transition-colors ${
                                                    active
                                                        ? "border-[#FF2E2E] bg-[#FF2E2E] text-white"
                                                        : "border-white/10 text-neutral-300 hover:border-white/40"
                                                }`}
                                            >
                                                {s}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                    Tags objectif
                                </Label>
                                <div className="mt-2 flex gap-2">
                                    {["wellness", "competition"].map((o) => {
                                        const active = profile.objective_tags.includes(o);
                                        return (
                                            <button
                                                key={o}
                                                onClick={() => toggleObj(o)}
                                                className={`border px-3 py-1 text-xs uppercase tracking-widest transition-colors ${
                                                    active
                                                        ? "border-[#FF2E2E] bg-[#FF2E2E] text-white"
                                                        : "border-white/10 text-neutral-300 hover:border-white/40"
                                                }`}
                                            >
                                                {o === "wellness" ? "Bien-être" : "Compétition"}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="border border-dashed border-white/10 p-4 text-[10px] uppercase tracking-[0.3em] text-neutral-600">
                                Les liens sociaux et compteurs d&apos;abonnés ne sont volontairement pas supportés.
                            </div>
                            <Button
                                data-testid={DASH.saveProfile}
                                onClick={saveProfile}
                                className="h-11 w-full rounded-none bg-[#FF2E2E] font-display text-lg uppercase tracking-tight hover:bg-[#D61F1F]"
                            >
                                Enregistrer le profil
                            </Button>
                        </div>
                    </div>
                </TabsContent>

                {/* STUDENTS / REQUESTS */}
                <TabsContent value="students" className="mt-6">
                    <div className="border border-white/10 bg-[#121212]">
                        <div className="flex items-center justify-between border-b border-white/10 p-5">
                            <h3 className="font-display text-2xl uppercase tracking-tight text-white">
                                Demandes de découverte
                            </h3>
                            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                {requests.length} au total
                            </span>
                        </div>
                        {requests.length === 0 ? (
                            <div className="p-6 text-sm text-neutral-500">
                                Aucune demande pour l&apos;instant. Les réservations apparaîtront ici.
                            </div>
                        ) : (
                            <ul className="divide-y divide-white/10">
                                {requests.map((r) => (
                                    <li key={r.request_id} className="p-5">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-display text-xl uppercase text-white">
                                                    {r.name}
                                                </div>
                                                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                                    {r.email}
                                                </div>
                                            </div>
                                            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                                {new Date(r.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="mt-3 text-sm text-neutral-300">{r.message}</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </TabsContent>

                {/* UPLOADS */}
                <TabsContent value="uploads" className="mt-6">
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                        <form
                            onSubmit={submitProg}
                            className="lg:col-span-2 space-y-3 border border-white/10 bg-[#121212] p-6"
                        >
                            <h3 className="font-display text-2xl uppercase tracking-tight text-white">
                                Publier une victoire
                            </h3>
                            <Input
                                data-testid={DASH.uploadStudent}
                                placeholder="Prénom de l'élève"
                                required
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                                value={prog.student_name}
                                onChange={(e) =>
                                    setProg({ ...prog, student_name: e.target.value })
                                }
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <Input
                                    data-testid="dash-upload-age"
                                    type="number"
                                    placeholder="Âge"
                                    className="rounded-none border-white/10 bg-[#0a0a0a]"
                                    value={prog.student_age}
                                    onChange={(e) =>
                                        setProg({ ...prog, student_age: e.target.value })
                                    }
                                />
                                <select
                                    data-testid="dash-upload-badge"
                                    value={prog.badge}
                                    onChange={(e) => setProg({ ...prog, badge: e.target.value })}
                                    className="h-10 rounded-none border border-white/10 bg-[#0a0a0a] px-3 text-sm text-white focus:border-[#FF2E2E] focus:outline-none"
                                >
                                    <option value="objectif_atteint">Objectif Atteint</option>
                                    <option value="avant_apres">Avant / Après</option>
                                </select>
                            </div>
                            <Input
                                data-testid={DASH.uploadDuration}
                                placeholder="Durée — ex : 6 mois de coaching"
                                required
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                                value={prog.duration_label}
                                onChange={(e) =>
                                    setProg({ ...prog, duration_label: e.target.value })
                                }
                            />
                            <Textarea
                                data-testid={DASH.uploadHeadline}
                                rows={3}
                                placeholder="Titre de la victoire — ex : a couru son premier 10 km"
                                required
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                                value={prog.headline}
                                onChange={(e) =>
                                    setProg({ ...prog, headline: e.target.value })
                                }
                            />
                            <Input
                                data-testid={DASH.uploadMediaUrl}
                                placeholder="URL du média (image ou vidéo)"
                                required
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                                value={prog.media_url}
                                onChange={(e) =>
                                    setProg({ ...prog, media_url: e.target.value })
                                }
                            />
                            <label className="flex items-start gap-3 border border-white/10 p-3">
                                <Checkbox
                                    data-testid={DASH.uploadConsent}
                                    checked={prog.consent}
                                    onCheckedChange={(v) => setProg({ ...prog, consent: !!v })}
                                    className="mt-0.5"
                                />
                                <span className="text-xs uppercase tracking-widest text-neutral-300">
                                    J&apos;ai le consentement écrit de cet·te élève pour publier
                                    sa progression. Obligatoire.
                                </span>
                            </label>
                            <Button
                                data-testid={DASH.uploadSubmit}
                                type="submit"
                                className="h-11 w-full rounded-none bg-[#FF2E2E] font-display text-lg uppercase tracking-tight hover:bg-[#D61F1F]"
                            >
                                Publier dans le fil
                            </Button>
                        </form>

                        <div className="lg:col-span-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {progress.length === 0 ? (
                                <div className="text-sm text-neutral-500 sm:col-span-2">
                                    Aucune progression publiée pour l&apos;instant.
                                </div>
                            ) : (
                                progress.map((p) => (
                                    <div
                                        key={p.item_id}
                                        className="border border-white/10 bg-[#121212]"
                                    >
                                        <div className="aspect-video overflow-hidden">
                                            <img
                                                src={p.media_url}
                                                alt=""
                                                className="h-full w-full object-cover"
                                            />
                                        </div>
                                        <div className="p-3">
                                            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                                {p.student_name} · {p.duration_label}
                                            </div>
                                            <div className="mt-1 font-display text-lg uppercase leading-tight text-white">
                                                {p.headline}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </TabsContent>

                {/* TESTIMONIAL (video + quote) */}
                <TabsContent value="testimonial" className="mt-6">
                    <form
                        onSubmit={saveTestimonial}
                        className="grid grid-cols-1 gap-6 lg:grid-cols-5"
                    >
                        <div className="space-y-3 lg:col-span-2 border border-white/10 bg-[#121212] p-6">
                            <h3 className="font-display text-2xl uppercase tracking-tight text-white">
                                Avis vidéo d&apos;un·e élève vérifié·e
                            </h3>
                            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Un témoignage court et sincère vaut mille followers.
                            </p>
                            <Input
                                data-testid="testim-student"
                                placeholder="Prénom de l'élève"
                                required
                                value={testim.student}
                                onChange={(e) => setTestim({ ...testim, student: e.target.value })}
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                            />
                            <Input
                                data-testid="testim-duration"
                                placeholder="Durée — ex : 6 mois"
                                value={testim.duration}
                                onChange={(e) => setTestim({ ...testim, duration: e.target.value })}
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                            />
                            <Textarea
                                data-testid="testim-quote"
                                placeholder="Citation écrite de l'élève"
                                required
                                rows={5}
                                value={testim.quote}
                                onChange={(e) => setTestim({ ...testim, quote: e.target.value })}
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                            />
                            <label className="flex items-start gap-3 border border-white/10 p-3">
                                <Checkbox
                                    data-testid="testim-consent"
                                    checked={testim.consent}
                                    onCheckedChange={(v) => setTestim({ ...testim, consent: !!v })}
                                    className="mt-0.5"
                                />
                                <span className="text-xs uppercase tracking-widest text-neutral-300">
                                    J&apos;ai le consentement écrit de l&apos;élève pour publier
                                    sa citation et sa vidéo. Obligatoire.
                                </span>
                            </label>
                            <Button
                                data-testid="testim-save"
                                type="submit"
                                className="h-11 w-full rounded-none bg-[#FF2E2E] font-display text-lg uppercase tracking-tight hover:bg-[#D61F1F]"
                            >
                                Enregistrer le témoignage
                            </Button>
                        </div>
                        <div className="lg:col-span-3">
                            <VideoRecorder
                                value={testim.video_data_url}
                                onChange={(v) => setTestim({ ...testim, video_data_url: v })}
                            />
                            <p className="mt-4 border border-dashed border-white/10 p-3 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                La vidéo apparaît à côté de la citation sur ton profil
                                public. Max 15 secondes · ~4 Mo.
                            </p>
                        </div>
                    </form>
                </TabsContent>
            </Tabs>
        </div>
    );
}
